import React, {useMemo, useState} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {Alert, FlexColumn, FlexRow, Modal, Text} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {ExpenseRow} from '../ExpenseTypes';
import type {ExpenseItemChangeProjectPhaseModal_viewer$data as ExpenseItemChangeProjectPhaseModal_viewer} from './__generated__/ExpenseItemChangeProjectPhaseModal_viewer.graphql';
import ConditionalDropdown from '../../dropdowns/conditional-dropdown/ConditionalDropdown';
import {getNotNullishNodes} from '../../../forecast-app/shared/util/NotNullPredicate';
import BulkUpdateExpenseItemsMutation from '../../../mutations/bulk_update_expense_items_mutation';
import Util from '../../../forecast-app/shared/util/util';
import {FieldLabel, PlaceholderItem} from './ModalComponents';
import ProjectIndicator from '../../../forecast-app/shared/components/project-indicator/ProjectIndicator';

type ExpenseItemChangeProjectPhaseModalProps = {
	expenseItems: ExpenseRow[];
	closeModal: () => void;
	viewer: ExpenseItemChangeProjectPhaseModal_viewer;
};

type Project = NonNullable<
	NonNullable<NonNullable<NonNullable<ExpenseItemChangeProjectPhaseModal_viewer['projects']>['edges']>[0]>['node']
>;

type Phase = NonNullable<
	NonNullable<NonNullable<NonNullable<ExpenseItemChangeProjectPhaseModal_viewer['phases']>['edges']>[0]>['node']
>;

const ProjectItem = ({project}: {project: Project}) => (
	<FlexRow gap={'m'}>
		<ProjectIndicator project={project} />
		<Text ellipsis>{project.name}</Text>
	</FlexRow>
);

const PhaseItem = ({phase}: {phase: Phase}) => (
	<FlexRow>
		<Text ellipsis>{phase.name}</Text>
	</FlexRow>
);

const findCommonProject = (expenseItems: ExpenseRow[], projects: Project[]) => {
	if (expenseItems.length === 0) {
		return;
	}
	const firstProjectId = expenseItems[0].project?.id;
	const commonProjectId = expenseItems.every(expenseItem => expenseItem.project?.id === firstProjectId)
		? firstProjectId
		: undefined;
	return commonProjectId ? projects.find(project => project.id === commonProjectId) : undefined;
};

const findCommonPhase = (expenseItems: ExpenseRow[], phases: Phase[]) => {
	if (expenseItems.length === 0) {
		return;
	}
	const firstPhaseId = expenseItems[0].phase?.id;
	const commonPhaseId = expenseItems.every(expenseItem => expenseItem.phase?.id === firstPhaseId) ? firstPhaseId : undefined;
	return commonPhaseId ? phases.find(phase => phase.id === commonPhaseId) : undefined;
};

const ExpenseItemChangeProjectPhaseModal = ({expenseItems, closeModal, viewer}: ExpenseItemChangeProjectPhaseModalProps) => {
	const {formatMessage} = useIntl();
	const updateableItems = useMemo(() => expenseItems.filter(expenseItem => !expenseItem.locked), [expenseItems]);
	const projects = useMemo(() => getNotNullishNodes(viewer.projects?.edges), [viewer]);
	const commonProject = useMemo(
		() => findCommonProject(updateableItems.length ? updateableItems : expenseItems, projects),
		[updateableItems, expenseItems, projects]
	);
	const [project, setProject] = useState<Project | undefined>(commonProject);
	const phases = useMemo(
		() => (project ? getNotNullishNodes(viewer.phases?.edges).filter(phase => phase.projectId === project.id) : []),
		[project, viewer]
	);
	const commonPhase = useMemo(
		() => findCommonPhase(updateableItems.length ? updateableItems : expenseItems, phases),
		[updateableItems, expenseItems, phases]
	);
	const [phase, setPhase] = useState<Phase | undefined>(commonPhase);

	const onChangeProject = (newProject: Project) => {
		setProject(newProject);
		setPhase(undefined);
	};

	const count = expenseItems.length;
	const lockedCount = expenseItems.length - updateableItems.length;
	const showAlert = lockedCount > 0;
	const allowSave = project && count > lockedCount;

	const saveChanges = () => {
		const onSuccess = () => {
			closeModal();
		};

		Util.CommitMutation(
			BulkUpdateExpenseItemsMutation,
			{
				ids: updateableItems.map(expenseItem => expenseItem.id),
				projectId: project?.id,
				phaseId: phase?.id,
			},
			onSuccess
		);
	};

	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={closeModal}>
			<Modal.Title>
				<FormattedMessage id="expense_management.change_project_phase.title" />
			</Modal.Title>
			<Modal.Content>
				<FlexColumn gap={'xl'}>
					<FieldLabel
						labelId={'common.project'}
						helperTextId={'expense_management.change_project_phase.project_helper'}
					>
						<ConditionalDropdown
							options={projects}
							useTriggerWidth
							autoSelect={false}
							renderOption={project => <ProjectItem project={project} />}
							onSelect={onChangeProject}
							data-cy={'select-project'}
						>
							{project ? (
								<ProjectItem project={project} />
							) : (
								<PlaceholderItem messageId={'expense_management.change_project_phase.project_placeholder'} />
							)}
						</ConditionalDropdown>
					</FieldLabel>

					<FieldLabel
						optional
						labelId={'common.phase'}
						helperTextId={phases.length ? 'expense_management.change_project_phase.phase_helper' : undefined}
					>
						<ConditionalDropdown
							options={phases}
							useTriggerWidth
							autoSelect={false}
							renderOption={phase => <PhaseItem phase={phase} />}
							onSelect={setPhase}
							data-cy={'select-phase'}
						>
							{phase ? (
								<PhaseItem phase={phase} />
							) : (
								<PlaceholderItem messageId={'expense_management.change_project_phase.phase_placeholder'} />
							)}
						</ConditionalDropdown>
					</FieldLabel>
					{showAlert && (
						<Alert
							text={formatMessage(
								{id: 'expense_management.change_project_phase.locked_alert'},
								{count, lockedCount}
							)}
							type={Alert.TYPE.INFORMATION}
						/>
					)}
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={saveChanges} disabled={!allowSave} dataCy={'confirm'}>
					<FormattedMessage id={'expense_management.actions.confirm_changes'} />
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal} dataCy={'cancel'}>
					<FormattedMessage id={'common.cancel'} />
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export const ExpenseItemChangeProjectPhaseModalQuery = graphql`
	query ExpenseItemChangeProjectPhaseModalQuery {
		viewer {
			actualPersonId
			component(name: "ExpenseItemChangeProjectPhaseModal")
			...ExpenseItemChangeProjectPhaseModal_viewer
		}
	}
`;

export default createFragmentContainer(ExpenseItemChangeProjectPhaseModal, {
	viewer: graphql`
		fragment ExpenseItemChangeProjectPhaseModal_viewer on Viewer {
			id
			company {
				id
			}
			phases {
				edges {
					node {
						id
						name
						projectId
					}
				}
			}
			projects(first: 100000) @connection(key: "Viewer_projects", filters: []) {
				edges {
					node {
						id
						name
						projectColor
						companyProjectId
						...ProjectIndicator_project
					}
				}
			}
		}
	`,
});
